import { css } from '@emotion/react';
import { PersonHelpers, PersonQueries, PersonTypes } from '@frontend/api-person';
import { AvatarCropper, readFileAsDataUrl } from '@frontend/avatar-image-cropper';
import { ContactChip } from '@frontend/contact-chip';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { getFullName } from '@frontend/name';
import { Photos, usePhotoStore } from '@frontend/photos';
import { useContactPanelStore } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Button, Heading, SkeletonLoader, styles, Text } from '@frontend/design-system';
import { useScheduleRequestModalFormContext } from '../../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/Context/ScheduleRequestModalFormContext';
import { ScheduleRequestsTrackingIds } from '../../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/trackingIds';

export const ContactCard = () => {
  const { t } = useTranslation('scheduleCalendarRequest');
  const { selectedPerson: person, personSelectorProps } = useScheduleRequestModalFormContext();
  const { householdId } = useContactPanelStore();
  const setPhotoRecord = usePhotoStore((state) => state.setPhotoRecord);

  const { mutateAsync: uploadMutation, isLoading: uploadLoading } = PersonQueries.useUploadPersonImage();

  if (!person) return;

  const isCustomContact = PersonTypes.PersonSourceType.CustomContact === person.SourceType;

  return (
    <>
      <div css={{ display: 'flex', gap: theme.spacing(2) }}>
        <SkeletonLoader shape='circle' width={64} height={64}>
          {isCustomContact ? (
            <AvatarCropper
              PersonID={person.PersonID}
              FirstName={person.FirstName}
              LastName={person.LastName}
              loading={uploadLoading}
              onCropComplete={(image) => {
                const personId = person.PersonID;
                if (!personId) throw new Error('no person id');
                readFileAsDataUrl(image, (photoUrl) => setPhotoRecord(personId, photoUrl));
                uploadMutation({ personId, image });
              }}
            />
          ) : (
            <Photos.ProfilePhoto
              disableClick
              size='xl'
              personId={person.PersonID}
              name={`${getFullName(person)}${householdId === person.PMID ? ' (HoH)' : ''}`}
            >
              {householdId === person.PMID && (
                <span css={householdBadgeStyles}>
                  <Icon name='crown' />
                </span>
              )}
            </Photos.ProfilePhoto>
          )}
        </SkeletonLoader>
        <section
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(1),
            width: '100%',
          }}
        >
          <div>
            <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
              <Heading
                level={2}
                css={css([
                  styles.truncate,
                  {
                    lineHeight: '32px',
                    maxWidth: 'calc(100% - 40px)',
                  },
                ])}
              >
                <span>{person.FirstName}</span>{' '}
                <span
                  css={{
                    fontWeight: theme.font.weight.regular,
                  }}
                >
                  {person.LastName}
                </span>
              </Heading>
              <SkeletonLoader css={{ marginTop: theme.spacing(1) }} isText width={100}>
                <div
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing(2),
                    '& > *': {
                      lineHeight: '16px',
                    },
                  }}
                >
                  {person.Status && (
                    <Text as={'div'} title={person.Status}>
                      <ContactChip.Status status={person.Status} />
                    </Text>
                  )}
                  {PersonHelpers.getAge(person) && (
                    <Text size='medium' color='subdued'>
                      {t('{{age}} yrs', { age: PersonHelpers.getAge(person) })}
                    </Text>
                  )}
                  {person.Gender && person.Gender !== 'Unknown' && (
                    <Text size='medium' color='subdued'>
                      {person.Gender}
                    </Text>
                  )}
                </div>
              </SkeletonLoader>
            </div>
          </div>
        </section>
      </div>
      <Button
        css={{ marginTop: theme.spacing(2) }}
        variant='secondary'
        size='large'
        iconName='link'
        trackingId={ScheduleRequestsTrackingIds.unlinkPatientButton}
        onClick={() => personSelectorProps.setSelectedPerson(null)}
      >
        {t('Unlink Patient')}
      </Button>
    </>
  );
};

const householdBadgeStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: -3px;
  right: -7px;
  height: 20px;
  width: 20px;
  background-color: ${theme.colors.warning10};
  border-radius: ${theme.borderRadius.full};
  box-shadow: ${theme.shadows.light};
  color: ${theme.colors.warning60};
`;
